import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getConversationTrace } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, AlertCircle, FileSearch, Zap, Search, Brain, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

import { FlowDiagram } from './FlowDiagram';
import { PhaseDetails } from './PhaseDetails';
import { transformTraceToFlowData, PHASE_CONFIG } from './utils';

interface WorkflowTraceFlowProps {
  conversationId: string;
}

// Phase icons mapping
const phaseIcons: Record<string, typeof Zap> = {
  phase1: Zap,
  phase2: Search,
  phase3: FileSearch,
  phase4a: Brain,
  phase4b: Sparkles,
};

// Phase colors mapping
const phaseColors: Record<string, string> = {
  phase1: 'text-blue-500',
  phase2: 'text-green-500',
  phase3: 'text-yellow-500',
  phase4a: 'text-purple-500',
  phase4b: 'text-pink-500',
};

export function WorkflowTraceFlow({ conversationId }: WorkflowTraceFlowProps) {
  const { data: trace, isLoading, error } = useQuery({
    queryKey: ['conversation-trace', conversationId],
    queryFn: () => getConversationTrace(conversationId),
    enabled: !!conversationId,
  });

  // Auto-select first available phase
  const defaultSelectedPhase = useMemo(() => {
    if (trace?.hasTrace && trace.phase1) {
      return 'phase1';
    }
    return null;
  }, [trace]);

  const [selectedPhase, setSelectedPhase] = useState<string | null>(defaultSelectedPhase);

  // Transform trace data to flow format
  const { nodes, edges } = useMemo(
    () => transformTraceToFlowData(trace, selectedPhase),
    [trace, selectedPhase]
  );

  // Update nodes when selection changes
  const nodesWithSelection = useMemo(
    () => nodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        isSelected: node.id === selectedPhase,
      },
    })),
    [nodes, selectedPhase]
  );


  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading trace data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8 text-red-500">
        <AlertCircle className="h-5 w-5 mr-2" />
        <span className="text-sm">Failed to load trace data</span>
      </div>
    );
  }

  if (!trace?.hasTrace) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <FileSearch className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">{trace?.message || 'No trace data available for this conversation.'}</p>
        <p className="text-xs mt-1 opacity-75">Trace data is only available for conversations after the Agent Trace feature was enabled.</p>
      </div>
    );
  }

  // Get selected phase config
  const selectedConfig = PHASE_CONFIG.find(p => p.key === selectedPhase);
  const SelectedIcon = selectedPhase ? phaseIcons[selectedPhase] : null;
  const selectedColor = selectedPhase ? phaseColors[selectedPhase] : '';

  return (
    <div className="grid grid-cols-2 gap-4 h-[600px]">
      {/* Left: Flow Diagram */}
      <Card className="overflow-hidden">
        <CardHeader className="py-3 border-b">
          <CardTitle className="text-sm font-medium">Workflow Flow</CardTitle>
        </CardHeader>
        <CardContent className="p-0 h-[calc(100%-49px)]">
          <FlowDiagram
            nodes={nodesWithSelection}
            edges={edges}
            onNodeClick={setSelectedPhase}
          />
        </CardContent>
      </Card>

      {/* Right: Phase Details */}
      <Card className="overflow-hidden">
        <CardHeader className="py-3 border-b">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            {SelectedIcon && <SelectedIcon className={cn('h-4 w-4', selectedColor)} />}
            {selectedConfig ? (
              <span>Phase {selectedConfig.phaseNumber}: {selectedConfig.name}</span>
            ) : (
              <span>Phase Details</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 h-[calc(100%-49px)]">
          {selectedPhase ? (
            <ScrollArea className="h-full">
              <div className="p-4">
                <PhaseDetails phaseKey={selectedPhase} trace={trace} />
              </div>
            </ScrollArea>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <p className="text-sm">Click a phase node to view details</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
